import React, { useState } from 'react';
import classnames from 'classnames';

// Types
import {
  FormTypeType,
  QuestionType,
  QuestionTypeType,
  ReviewFormTemplateType,
} from 'Shared/types/ReviewFormTemplate.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useQuestion } from './use-question';

// Components
import { WpContainer, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import WpSelectV2, { StrictOption } from 'Wpds/components/select/WpSelectV2';
import { WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpToggleSwitch } from 'Wpds/components/toggle-switch';
import { WpCollapsible } from 'Wpds/components/collapsible';
import WpInlineError from 'Wpds/components/inline-error/WpInlineError';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';

// Styles
import './QuestionWrapper.scss';

const FeedbackFormQuestionOptions: StrictOption[] = [
  { label: 'Short Answer', value: QuestionTypeType.TEXTAREA },
  { label: 'Rating', value: QuestionTypeType.RATING },
  { label: 'Gif Selection', value: QuestionTypeType.GIF },
];

const PeerReviewFormQuestionOptions: StrictOption[] = [
  { label: 'Short Answer', value: QuestionTypeType.TEXTAREA },
  { label: 'Rating', value: QuestionTypeType.RATING },
  { label: 'Adjectives Selection', value: QuestionTypeType.ADJECTIVES_FOR_PROPERTY },
  { label: 'Gif Selection', value: QuestionTypeType.GIF },
];

const CompilationFormQuestionOptions: StrictOption[] = [
  { label: 'Short Answer', value: QuestionTypeType.TEXTAREA },
  { label: 'Rating', value: QuestionTypeType.RATING },
  { label: 'Adjectives Selection', value: QuestionTypeType.ADJECTIVES_FOR_PROPERTY },
  { label: 'Gif Selection', value: QuestionTypeType.GIF },
  { label: 'Rating Summary', value: QuestionTypeType.RATING_SUMMARY },
  { label: 'Adjectives Selection Summary', value: QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY },
  { label: 'AI Summary', value: QuestionTypeType.HOLISTIC_SUMMARY },
];

type Props = {
  question: QuestionType;
  reviewFormType: FormTypeType;
  reviewFormTemplates: ReviewFormTemplateType[];
  children: React.ReactNode;
  onChange: Function;
  handleDeleteQuestion: Function;
  handleDuplicateQuestion: Function;
  handleMoveQuestion: Function;
  activeQuestionKey: string;
  setActiveQuestionKey: Function;
};

const QuestionWrapper = ({
  question,
  reviewFormType,
  reviewFormTemplates,
  children,
  onChange,
  handleDeleteQuestion,
  handleDuplicateQuestion,
  handleMoveQuestion,
  activeQuestionKey,
  setActiveQuestionKey,
}: Props) => {
  const { errors } = useQuestion({ question });
  const [header, setHeader] = useState(question.header);

  const showSelfReviewDescriptionAndVisibility = reviewFormType === FormTypeType.peer_review;

  const [description, setDescription] = useState(question.description);
  const [selfReviewDescription, setSelfReviewDescription] = useState(question.selfReviewDescription);
  const [visibility, setVisibility] = useState(question.visibility || 'everyone');

  const [required, setRequired] = useState<boolean>(question?.optional !== undefined ? !question?.optional : false);
  const [isDeleting, toggleIsDeleting] = useToggle(false);

  const isActive = activeQuestionKey === question.key;

  const hasErrors = errors?.length > 0;

  const peerReviewFormTemplate = reviewFormTemplates.find(({ formType }) => formType == FormTypeType.peer_review);

  const questionTypeOptions = {
    [FormTypeType.compilation]: CompilationFormQuestionOptions,
    [FormTypeType.peer_review]: PeerReviewFormQuestionOptions,
    [FormTypeType.feedback]: FeedbackFormQuestionOptions,
  }[reviewFormType];

  const handleUpdateType = value => {
    if (
      value === QuestionTypeType.RATING_SUMMARY &&
      !(
        peerReviewFormTemplate &&
        peerReviewFormTemplate.questions.find(question => question.type === QuestionTypeType.RATING)
      )
    ) {
      alert('You must create a rating question in the peer review before you can create a rating summary question.');
      return;
    }
    if (
      value === QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY &&
      !(
        peerReviewFormTemplate &&
        peerReviewFormTemplate.questions.find(question => question.type === QuestionTypeType.ADJECTIVES_FOR_PROPERTY)
      )
    ) {
      alert(
        'You must create an adjective question in the peer review before you can create a adjectives summary question.'
      );
      return;
    }

    const summarizerQuestion = reviewFormTemplates
      .map(reviewFormTemplate => reviewFormTemplate.questions)
      .flat()
      .find(({ summarizedQuestionKey }) => summarizedQuestionKey === question.key);
    if (summarizerQuestion) {
      if (
        (summarizerQuestion.type === QuestionTypeType.RATING_SUMMARY && value !== QuestionTypeType.RATING) ||
        (summarizerQuestion.type === QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY &&
          value !== QuestionTypeType.ADJECTIVES_FOR_PROPERTY)
      ) {
        alert(
          "You cannot change this question's type because it is linked to a summary question. Please delete the summary question first."
        );
        return;
      }
    }

    const additionalChanges =
      value === QuestionTypeType.RATING_SUMMARY || value === QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY
        ? {
            summarizedQuestionKey: peerReviewFormTemplate.questions.find(
              question =>
                question.type ===
                (value === QuestionTypeType.RATING_SUMMARY
                  ? QuestionTypeType.RATING
                  : QuestionTypeType.ADJECTIVES_FOR_PROPERTY)
            ).key,
          }
        : {};
    onChange({ type: value, ...additionalChanges });
  };

  const handleUpdateHeader = value => {
    setHeader(value);
    onChange({ header: value });
  };

  const handleUpdateDescription = value => {
    setDescription(value);
    onChange({ description: value });
  };

  const handleUpdateSelfReviewDescription = value => {
    setSelfReviewDescription(value);
    onChange({ selfReviewDescription: value });
  };

  const handleUpdateVisibility = value => {
    setVisibility(value);
    onChange({ visibility: value });
  };

  const handleUpdateRequired = (value: boolean) => {
    setRequired(value);
    onChange({ optional: !value });
  };

  const handleDeleteQuestionClick = () => {
    if (
      reviewFormTemplates.find(reviewFormTemplate =>
        reviewFormTemplate.questions.find(({ summarizedQuestionKey }) => summarizedQuestionKey === question.key)
      )
    ) {
      alert(
        'You cannot delete this question because it is linked to a summary question. Please delete the summary question first.'
      );
      return;
    }

    toggleIsDeleting();
    handleDeleteQuestion({ question });
  };

  const handleDuplicateClick = () => {
    handleDuplicateQuestion({ question });
  };

  const handleSetActiveQuestionKey = () => {
    setActiveQuestionKey(question.key);
  };

  const handleMoveUpClick = () => {
    handleMoveQuestion({ direction: 'up', question });
  };

  const handleMoveDownClick = () => {
    handleMoveQuestion({ direction: 'down', question });
  };

  const baseClassName = 'QuestionWrapper';
  const classes = classnames(baseClassName, isActive ? `${baseClassName}--active` : '');

  return (
    <div className={classes}>
      <div className={`${baseClassName}__wrapper`}>
        <WpContainer
          className={`${baseClassName}__wrapper__container`}
          onClick={!isActive ? handleSetActiveQuestionKey : null}
        >
          <WpContainerBody>
            {hasErrors && (
              <div className={`${baseClassName}__error`}>
                <span className={`${baseClassName}__error__icon`}>
                  <WpIcon name="exclamation-circle" />
                </span>
                <WpInlineError error={errors[0].label} />
              </div>
            )}
            {!isActive && (
              <div className={`${baseClassName}__inactive`}>
                <p className={`${baseClassName}__inactive__title`}>
                  {header || 'Question'}
                  {required && <span className={`${baseClassName}__inactive__title__required`}>*</span>}
                </p>
                {description && <p className={`${baseClassName}__inactive__desc`}>{description}</p>}
              </div>
            )}
            <WpCollapsible open={isActive}>
              <WpFormLayout direction="vertical" spacing="md">
                <div className={`${baseClassName}__question-select`}>
                  <WpSelectV2
                    id={`question_type_${question.key}`}
                    label="Question type"
                    placeholder="Select question type"
                    options={questionTypeOptions}
                    value={question.type}
                    onChange={handleUpdateType}
                    forceReset
                  />
                </div>
                <WpTextField
                  id={`title_${question.key}`}
                  label="Title"
                  value={header}
                  placeholder="Question title"
                  onChange={handleUpdateHeader}
                  required
                />
                {showSelfReviewDescriptionAndVisibility && (
                  <>
                    <WpTextField
                      id={`description_${question.key}`}
                      label="Description"
                      value={description}
                      placeholder="Description"
                      multiline={2}
                      onChange={handleUpdateDescription}
                    />
                    <WpTextField
                      id={`self_review_description_${question.key}`}
                      label="Description for self feedback"
                      value={selfReviewDescription}
                      placeholder="Self Feedback Description"
                      multiline={2}
                      onChange={handleUpdateSelfReviewDescription}
                    />
                    <WpRadioGroup
                      id={`visibility_${question.key}`}
                      label="Who should this question be shown to?"
                      defaultValue={visibility}
                      onChange={handleUpdateVisibility}
                      direction="horizontal"
                    >
                      <WpRadioInput
                        id={`self_and_non_self_review_visibility_${question.key}`}
                        label="All feedback providers"
                        value={'everyone'}
                      />
                      <WpRadioInput
                        id={`self_review_visibility_${question.key}`}
                        label="Self feedback providers"
                        value="self_review"
                      />
                      <WpRadioInput
                        id={`non_self_review_visibility_${question.key}`}
                        label="Non self feedback providers"
                        value="non_self_review"
                      />
                    </WpRadioGroup>
                  </>
                )}
                {!showSelfReviewDescriptionAndVisibility && (
                  <WpTextField
                    id={`description_${question.key}`}
                    label="Description"
                    value={description}
                    placeholder="Description"
                    multiline={2}
                    onChange={handleUpdateDescription}
                  />
                )}
              </WpFormLayout>
            </WpCollapsible>
            {children}
          </WpContainerBody>
          {isActive && (
            <WpContainerFooter justifyContent="spaceBetween">
              <WpToggleSwitch
                id={`optional_toggle_${question.key}`}
                label="Required"
                checked={required}
                onChange={handleUpdateRequired}
              />
              <WpButtonGroup>
                <WpButton variant="tertiary" onClick={handleDuplicateClick}>
                  <WpIcon name="copy" />
                </WpButton>
                <WpButton variant="tertiary" onClick={handleDeleteQuestionClick} loading={isDeleting}>
                  <WpIcon name="trash" />
                </WpButton>
              </WpButtonGroup>
            </WpContainerFooter>
          )}
        </WpContainer>
        <WpContainer className={`${baseClassName}__move-buttons`}>
          <WpContainerBody padding="025">
            <WpButtonGroup stacked spacing="sm">
              <WpButton variant="tertiary" onClick={handleMoveUpClick} size="sm">
                <WpIcon name="chevron-up" />
              </WpButton>
              <WpButton variant="tertiary" onClick={handleMoveDownClick} size="sm">
                <WpIcon name="chevron-down" />
              </WpButton>
            </WpButtonGroup>
          </WpContainerBody>
        </WpContainer>
      </div>
    </div>
  );
};

export default QuestionWrapper;
